import React, { useState, useEffect, useRef, useCallback } from 'react';
import OverlayPortal from '../OverlayPortal';
import { Search, X, Play, Pause, Plus, Loader2, Music, Pencil } from 'lucide-react';
const API = process.env.REACT_APP_BACKEND_URL || '';
/**
 * SongSearchModal — searches the free iTunes catalog via the STAFFED
 * /api/music/search proxy, plays 30s previews, and returns a chosen
 * "Title — Artist" string. Public endpoint, so it works for clients too.
 */
export default function SongSearchModal({ open, onClose, onSelect, onPickSong, fieldLabel, sections, sectionOf, onAddToSection, sectionFull }) {
  const sectionsMode = Array.isArray(sections) && sections.length > 0;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTitle, setManualTitle] = useState('');
  const [manualArtist, setManualArtist] = useState('');
  const audioRef = useRef(null);
  const debounceRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/music/search?q=${encodeURIComponent(q)}`);
        const data = await r.json();
        setResults(Array.isArray(data.results) ? data.results : []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setPlayingId(null);
      setManualOpen(false);
      setManualTitle('');
      setManualArtist('');
      if (audioRef.current) audioRef.current.pause();
    }
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const togglePreview = useCallback((song) => {
    const audio = audioRef.current;
    if (!audio || !song.previewUrl) return;
    if (playingId === song.id) {
      audio.pause();
      setPlayingId(null);
      return;
    }
    audio.src = song.previewUrl;
    audio.play().then(() => setPlayingId(song.id)).catch(() => setPlayingId(null));
  }, [playingId]);
  const pick = (song) => {
    if (audioRef.current) audioRef.current.pause();
    if (onPickSong) onPickSong(song);
    else if (onSelect) onSelect(`${song.title} — ${song.artist}`);
    onClose();
  };
  const openManual = () => {
    setManualTitle(query.trim());
    setManualArtist('');
    setManualOpen(true);
  };
  const fileManual = (sectionKey) => {
    const title = manualTitle.trim();
    if (!title) return;
    const song = { title, artist: manualArtist.trim(), source: 'manual' };
    if (audioRef.current) audioRef.current.pause();
    if (sectionsMode && onAddToSection) onAddToSection(song, sectionKey);
    else if (onPickSong) onPickSong(song);
    else if (onSelect) onSelect(`${title}${song.artist ? ` — ${song.artist}` : ''}`);
    onClose();
  };
  if (!open) return null;
  return (
    <OverlayPortal>
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="my-8 w-full max-w-lg bg-[#0B1224]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_16px_50px_-24px_rgba(0,0,0,0.85)] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.07]">
          <div className="flex items-center gap-2 min-w-0">
            <Music className="w-4 h-4 text-[#D4AF37] shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37]/60 font-semibold">Song search</p>
              {fieldLabel && <p className="text-[13px] font-semibold text-white leading-tight truncate">{fieldLabel}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors shrink-0" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by song or artist…"
              className="w-full bg-[#0A0F1F] border border-white/[0.08] rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:border-[#D4AF37]/40"
            />
          </div>
        </div>
        <div className="px-4 pb-4 max-h-[55vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-white/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
          )}
          {!loading && query.trim().length < 2 && results.length === 0 && (
            <div className="text-center py-10 text-white/30 text-[13px]">Type a song or artist to search.</div>
          )}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div className="text-center py-6 text-white/30 text-[13px]">No matches — add it by hand below.</div>
          )}
          {!loading && results.length > 0 && (
            <div className="space-y-1.5">
              {results.map((song) => (
                <div key={song.id} className="group flex items-center gap-3 rounded-xl border border-white/[0.06] hover:border-[#D4AF37]/25 bg-white/[0.02] p-2 transition-colors">
                  <button
                    type="button"
                    onClick={() => togglePreview(song)}
                    disabled={!song.previewUrl}
                    className="relative w-11 h-11 rounded-lg overflow-hidden shrink-0 disabled:opacity-60"
                    aria-label={playingId === song.id ? 'Pause preview' : 'Play preview'}
                  >
                    {song.artwork ? (
                      <img src={song.artwork} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-white/5 flex items-center justify-center"><Music className="w-4 h-4 text-white/30" /></div>
                    )}
                    {song.previewUrl && (
                      <span className={`absolute inset-0 flex items-center justify-center transition-opacity ${playingId === song.id ? 'bg-black/50 opacity-100' : 'bg-black/40 opacity-0 group-hover:opacity-100'}`}>
                        {playingId === song.id ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
                      </span>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-white truncate">{song.title}</p>
                    <p className="text-[11px] text-white/45 truncate">{song.artist}</p>
                  </div>
                  {sectionsMode ? (
                    <div className="flex items-center gap-1 shrink-0" data-testid={`song-search-sections-${song.id}`}>
                      {sections.map((sec) => {
                        const current = sectionOf ? sectionOf(song) : null;
                        const isActive = current === sec.key;
                        const full = !isActive && sectionFull && sectionFull[sec.key];
                        return (
                          <button
                            key={sec.key}
                            type="button"
                            onClick={() => { if (!full && onAddToSection) onAddToSection(song, sec.key); }}
                            disabled={full}
                            title={full ? `${sec.label} is full` : (isActive ? `In ${sec.label}` : `Add to ${sec.label}`)}
                            aria-label={full ? `${sec.label} is full` : (isActive ? `In ${sec.label}` : `Add to ${sec.label}`)}
                            className={`w-8 h-8 rounded-lg text-[15px] font-bold flex items-center justify-center transition active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed ${
                              isActive
                                ? 'bg-gradient-to-br from-[#E6C86A] to-[#C5A028]'
                                : 'bg-white/[0.05] border border-white/10 hover:bg-white/10'
                            }`}
                            style={{ color: isActive ? '#0B0F1A' : sec.iconColor }}
                            data-testid={`song-search-add-${sec.key}-${song.id}`}
                          >
                            <span aria-hidden="true" className="leading-none">{sec.icon}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                  <button
                    type="button"
                    onClick={() => pick(song)}
                    className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#E6C86A] to-[#C5A028] text-[#0B0F1A] flex items-center justify-center shrink-0 hover:brightness-110 active:scale-95 transition"
                    aria-label="Use this song"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {!loading && query.trim().length >= 1 && (
            <div className="mt-2 pt-2 border-t border-white/[0.06]" data-testid="song-manual-block">
              {!manualOpen ? (
                <button
                  type="button"
                  onClick={openManual}
                  className="w-full flex items-center gap-2 rounded-xl border border-dashed border-white/15 hover:border-[#D4AF37]/40 bg-white/[0.02] px-3 py-2.5 text-left transition"
                  data-testid="song-manual-open"
                >
                  <span className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><Pencil className="w-4 h-4 text-white/50" /></span>
                  <span className="text-[12.5px] text-white/70">
                    {results.length > 0 ? "Don't see it? Type a song in yourself." : <>Add “<span className="text-white font-medium">{query.trim()}</span>” by hand</>}
                  </span>
                </button>
              ) : (
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="song-manual-form">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[#D4AF37]/70 font-semibold">Type it in yourself</p>
                  <input
                    autoFocus
                    value={manualTitle}
                    onChange={(e) => setManualTitle(e.target.value)}
                    placeholder="Song title"
                    data-testid="song-manual-title"
                    className="w-full bg-[#0A0F1F] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/25 outline-none focus:border-[#D4AF37]/40"
                  />
                  <input
                    value={manualArtist}
                    onChange={(e) => setManualArtist(e.target.value)}
                    placeholder="Artist (optional)"
                    data-testid="song-manual-artist"
                    className="w-full bg-[#0A0F1F] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/25 outline-none focus:border-[#D4AF37]/40"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5" data-testid="song-manual-sections">
                      {(sectionsMode ? sections : []).map((sec) => (
                        <button
                          key={sec.key}
                          type="button"
                          onClick={() => fileManual(sec.key)}
                          disabled={!manualTitle.trim()}
                          title={`Add to ${sec.label}`}
                          aria-label={`Add to ${sec.label}`}
                          className="w-9 h-9 rounded-lg text-[15px] font-bold bg-white/[0.05] border border-white/10 hover:bg-white/10 flex items-center justify-center transition active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                          style={{ color: sec.iconColor }}
                          data-testid={`song-manual-add-${sec.key}`}
                        >
                          <span aria-hidden="true" className="leading-none">{sec.icon}</span>
                        </button>
                      ))}
                      {!sectionsMode && (
                        <button
                          type="button"
                          onClick={() => fileManual()}
                          disabled={!manualTitle.trim()}
                          className="px-3 h-9 rounded-lg bg-gradient-to-br from-[#E6C86A] to-[#C5A028] text-[#0B0F1A] text-[12px] font-semibold disabled:opacity-40"
                          data-testid="song-manual-add-plain"
                        >Add</button>
                      )}
                    </div>
                    <button type="button" onClick={() => setManualOpen(false)} className="text-[12px] text-white/45 hover:text-white/70 px-2 py-1" data-testid="song-manual-cancel">Cancel</button>
                  </div>
                  {!manualTitle.trim() && <p className="text-[10.5px] text-white/35">Add a song title to file it.</p>}
                </div>
              )}
            </div>
          )}
        </div>
        <audio ref={audioRef} onEnded={() => setPlayingId(null)} className="hidden" />
      </div>
    </div>
    </OverlayPortal>
  );
}
